import {Aggregate, CollectionView, Control, culture, DataType} from "@mescius/wijmo";
import {
    AllowDragging,
    AllowPinning,
    AllowResizing,
    AllowSorting, CellRange,
    CellRangeEventArgs,
    ClipStringOptions,
    Column,
    ColumnGroupCollection,
    DataMap,
    FlexGrid,
    HeadersVisibility,
    ICellTemplateFunction,
    SelectionMode
} from "@mescius/wijmo.grid";
import {FilterMode, FlexGridFilter} from '@mescius/wijmo.grid.filter';
import {signal, Signal} from '@angular/core';
import {AnnotationAttachment, AnnotationPosition} from '@mescius/wijmo.chart.annotation';
import {
    AxisType,
    ChartType,
    LabelPosition,
    OverlappingLabels,
    PieLabelPosition,
    Position,
    TickMark
} from '@mescius/wijmo.chart';
import {InteractiveAxes, MouseAction} from '@mescius/wijmo.chart.interaction';
import {ShowText} from '@mescius/wijmo.gauge';
import {range} from 'es-toolkit';
import {StringUtils} from './strings';

export type ColumnAlign = 'left' | 'center' | 'right';

export interface ColumnDeclaration {
    name?: string;
    header: string;
    binding?: string;
    editor?: Control;
    dataMap?: DataMap;
    format?: string;
    width?: number | string;
    height?: number | string;
    visible?: boolean;
    minWidth?: number;
    dataType?: DataType;
    align?: ColumnAlign;
    cssClass?: string;
    cssClassAll?: string;
    cellTemplate?: string | ICellTemplateFunction;
    isContentHtml?: boolean;
    isReadOnly?: boolean;
    isRequired?: boolean;
    columns?: ColumnDeclaration[];
    aggregate?: Aggregate;
    wordWrap?: boolean;
}

export interface ColumnGroupDeclaration extends ColumnDeclaration {
    collapseTo?: string[];
    isCollapsed?: boolean;
    columns?: (ColumnDeclaration | ColumnGroupDeclaration)[];
}

export interface GridSelectionSummary {
    numSelected: number;
    numNumeric: number;
    sumNumeric?: number;
    avgNumeric?: number;
}

const NO_SELECTION_SUMMARY: GridSelectionSummary = {
    numSelected: 0,
    numNumeric: 0,
    sumNumeric: undefined,
    avgNumeric: undefined,
};

export class Wijmo {

    static HeadersVisibility = HeadersVisibility;
    static AllowResizing = AllowResizing;
    static AllowDragging = AllowDragging;
    static AllowSorting = AllowSorting;
    static SelectionMode = SelectionMode;
    static AllowPinning = AllowPinning;
    static AxisType = AxisType;
    static InteractiveAxes = InteractiveAxes;
    static MouseAction = MouseAction;

    static ChartType = ChartType;

    static AnnotationPosition = AnnotationPosition;
    static AnnotationAttachment = AnnotationAttachment;

    static Position = Position;
    static PieLabelPosition = PieLabelPosition;
    static LabelPosition = LabelPosition;
    static OverlappingLabels = OverlappingLabels;

    static TickMark = TickMark;

    static FilterMode = FilterMode;
    static ShowText = ShowText;
}

class MsoTypeInfo {
    dataType: DataType | null;
    format: string | null;
    isPct: boolean;
    isHtml: boolean;

    constructor(column: Column) {
        this.dataType = column.dataType;
        this.format = column.format;
        this.isPct = column.format?.startsWith('p') ?? false;
        this.isHtml = column.isContentHtml ?? false;
    }

    toMsoClass(value: any): string | undefined {
        if (this.isPct) return 'num-pct';
        switch (this.dataType) {
            case DataType.Number: return 'num-gen';
            case DataType.Date: return 'date';
        }
        return undefined;
    }


}

export class WjGridUtils {

    // a global signal, to which one can subscribe to get the unformatted value of the selected cell
    // (see `WjGridUtils.registerSelectedCellValueSignal(grid)`)
    private static _selectedCellUnformattedValue = signal<any>(undefined);
    public  static selectedCellUnformattedValue = WjGridUtils._selectedCellUnformattedValue.asReadonly();

    private static _selectedRangeUnformattedValues = signal<any[][]>([[]]);
    public  static selectedRangeUnformattedValues = WjGridUtils._selectedRangeUnformattedValues.asReadonly();

    private static _selectedRangeSummary = signal<GridSelectionSummary>(NO_SELECTION_SUMMARY);
    public  static selectedRangeSummary = WjGridUtils._selectedRangeSummary.asReadonly();

    /**
     * Adds an array of columns to the specified grid and optionally clears existing columns.
     * Simplifies the standard approach to add columns via a utility method.
     */
    static addColumns(grid: FlexGrid, columns: ColumnDeclaration[], clearColumns = true) {
        if (clearColumns) {
            grid.columns.clear();
        }
        columns.forEach(col => grid.columns.push(new Column(col)));
    }

    /**
     * Same as addColumns() on the grid's 'groupColumns' attribute.
     */
    static addColumnGroups(grid: FlexGrid, columns: (ColumnDeclaration | ColumnGroupDeclaration)[]) {
        grid.columnGroups = columns;
    }

    /**
     * Unselects all rows and columns in the grid. Improves the look of tables, especially
     * when the grid is read-only and possibly multiple grids appear on a page because it
     * will take out the highlighted selected cell.
     */
    static unselectAll(grid: FlexGrid) {
        // this call is a bit funny because the select() method should always work - no?
        // but sometimes it doesn't work right when the grid is fresh or whatever ...
        try {
            grid.select(-1, -1);
        } catch (e) {
            // ignore
        }
    }

    private static copyExcelHtmlTemplate = `
            <html xmlns:v="urn:schemas-microsoft-com:vml"
                      xmlns:o="urn:schemas-microsoft-com:office:office"
                      xmlns:x="urn:schemas-microsoft-com:office:excel"
                      xmlns="http://www.w3.org/TR/REC-html40">

                <head>
                    <meta http-equiv=Content-Type content="text/html; charset=utf-8">
                    <meta name=ProgId content=Excel.Sheet>
                    <meta name=Generator content="Microsoft Excel 15">
                    <style>
                        <!--table
                        {mso-displayed-decimal-separator:"\${this.decimalSeparator}";
                         mso-displayed-thousand-separator:"\${this.thousandSeparator}";
                        @page
                        {margin:.79in .7in .79in .7in;
                            mso-header-margin:.3in;
                            mso-footer-margin:.3in;}
                        tr
                        {mso-height-source:auto;}
                        col
                        {mso-width-source:auto;}
                        br
                        {mso-data-placement:same-cell;}
                        td
                        {padding-top:1px;
                            padding-right:1px;
                            padding-left:1px;
                            mso-ignore:padding;
                            color:black;
                            font-size:11.0pt;
                            font-weight:400;
                            font-style:normal;
                            text-decoration:none;
                            font-family:"Aptos Narrow", sans-serif;
                            mso-font-charset:0;
                            mso-number-format:General;
                            text-align:general;
                            vertical-align:bottom;
                            border:none;
                            mso-background-source:auto;
                            mso-pattern:auto;
                            mso-protection:locked visible;
                            white-space:nowrap;
                            mso-rotate:0;}
                        .num-pct {mso-number-format:Percent;}
                        .num-gen {mso-number-format:General;}
                        .date {mso-number-format:"d/m/yyyy"}
                        -->
                    </style>
                </head>

                <body>
                    \${this.dataTable}
                </body>
            </html>
            `

    static x = ``;

    static activateCopyUnformattedMode(grid: FlexGrid) {
        grid.copying.addHandler((s, ex) => {
            WjGridUtils.copyRangeToClipboard(grid, ex.range, false);
            ex.cancel = true;
        });
        grid.pasting.addHandler((s, ex) => {
            // ... if at any time, we would like to paste formatted data, we can implement that here
        });
    }

    static copyRangeToClipboard(grid: FlexGrid, cellRange: CellRange, includeHeaders: Boolean) {
        const clipStringOpts = ClipStringOptions.Default;
        const textPlain = grid.getClipString(cellRange, clipStringOpts, false, false);

        const rowRange = range(cellRange.topRow, cellRange.bottomRow + 1);
        const colRange = range(cellRange.leftCol, cellRange.rightCol + 1);

        const visibleColumns = colRange.filter(i => grid.getColumn(i).isVisible);
        const typeInfos: MsoTypeInfo[] = visibleColumns.map(i => new MsoTypeInfo(grid.getColumn(i)));

        const rowElements = rowRange.map(row => {
            const tdElements = visibleColumns.map((col, idx) => {
                const td = document.createElement('td');
                const tdCls = typeInfos[idx].toMsoClass(typeInfos[idx]);
                td.innerText = grid.getCellData(row, col, true);
                if (tdCls) td.classList.add(tdCls);
                return td;
            });

            const tr = document.createElement('tr');
            tr.append(...tdElements);
            return tr;
        });


        const tableElement = document.createElement('table');
        const theadElement = document.createElement('thead');
        const tbodyElement = document.createElement('tbody');
        tbodyElement.append(...rowElements);
        tableElement.append(theadElement, tbodyElement);

        if (includeHeaders) {
            const thElements = visibleColumns.map(col => {
                const th = document.createElement('th');
                th.innerText = grid.columns[col].header ?? '-';
                return th;
            });
            const headerRow = document.createElement('tr');
            headerRow.append(...thElements);
            theadElement.append(headerRow);
        }

        const textHtml = StringUtils.fillTemplate(this.copyExcelHtmlTemplate, {
            decimalSeparator: '\\' + culture.Globalize.numberFormat['.'],
            thousandSeparator: '\\' + culture.Globalize.numberFormat[','],
            dataTable: tableElement.outerHTML
        });

        navigator.clipboard.write([new ClipboardItem({ 'text/plain': textPlain, 'text/html': textHtml})])
            .then(() => {
                const numberOfCellsCoppied = rowRange.length * colRange.length;
                console.log(`copied ${numberOfCellsCoppied} cells to clipboard`)
            });
    }

    static copyRangeToClipboardAsCsv(grid: FlexGrid, cellRange: CellRange, includeHeaders: Boolean) {
        const csv = grid.getClipString(grid.selection, ClipStringOptions.CSV|ClipStringOptions.Unformatted);
        let headerRow = ''

        if (includeHeaders) {
            const colRange = range(cellRange.leftCol, cellRange.rightCol + 1);
            const visibleColumns = colRange.filter(i => grid.getColumn(i).isVisible);
            headerRow = visibleColumns.map(i => grid.getColumn(i).header).join(',') + '\n';
        }

        navigator.clipboard.writeText(headerRow + csv)
            .then(() => console.log('data copied to clipboard'));
    }

    static treeWalk<T extends { columns: T[] }>(tree: T[], visitor: (col: T) => any) {
        tree.forEach(col => {
            visitor(col);
            if (col.columns) {
                this.treeWalk(col.columns, visitor);
            }
        });
    }

    private static findDeepColumnInternal(columns: ColumnGroupCollection, predicate: (column: Column) => boolean): Column | undefined {
        for (const col of columns) {
            if (predicate(col)) {
                return col;
            }
            if (col.columns) {
                const found = this.findDeepColumnInternal(col.columns, predicate);
                if (found) {
                    return found;
                }
            }
        }
        return undefined;
    }

    static findDeepColumn(grid: FlexGrid, predicate: (column: Column) => boolean): Column | undefined {
        return this.findDeepColumnInternal(grid.getColumnGroups(), predicate);
    }

    /**
     * Connects a FlexGrid with a data source provided as a Signal.
     * When used as an effect() during the component initialization, makes sure that,
     * when the data source changes, the grid's itemsSource is updated accordingly.
     * If a FlexGridFilter is provided, it re-applies the filter definitions after updating the itemsSource.
     *
     * Usage: ```typescript
     *   class ComponentA {
     *       constructor() {
     *           // connect grid and data source - attaches a listener to data source changes
     *           // do NOT set the grids itemSource manually after that!
     *           effect(() => connectGridWithSignalSource(<name>, grid, filter, signal-with-data));
     *       }
     *   }
     * ```
     *
     * @param name          The name of the grid to be used for logging purposes.
     * @param dataSignal    Signal providing the data source.
     * @param gridSignal    Signal providing the FlexGrid instance.
     * @param gridFilter    Signal providing the FlexGridFilter instance.
     * @returns             void
     */
    static connectGridWithSignalSource<T extends FlexGrid, V extends FlexGridFilter>(name: string, dataSignal: Signal<any>, gridSignal: Signal<T | undefined>, gridFilter?: (Signal<V | undefined>) | undefined) {
        const grid = gridSignal();
        const filter = gridFilter?.call(undefined);
        const data = dataSignal();
        const dataIsCollectionView = data instanceof CollectionView;
        const itemsSource = dataIsCollectionView ? data : new CollectionView(data);
        const gridItemsSource = grid?.itemsSource;

        if (grid && data && gridItemsSource !== data) {
            const filterDefinitions =  filter?.filterDefinition;
            console.debug(`Connecting Grid '${name}' with Signal Source`);
            WjGridUtils.updateGridItemsSourceAndPreserveSelection(grid, itemsSource);
            setTimeout(() => {
                if (filter && filterDefinitions) {
                    filter.filterDefinition = filterDefinitions;
                    filter.apply()
                }

            }, 0);
        }
    }

    static swapColumns(grid: FlexGrid, n1: number, n2: number) {
        const cg = grid.getColumnGroups();
        const temp = cg[n1];
        cg[n1] = cg[n2];
        cg[n2] = temp;
        grid.columnGroups = cg;
    }

    /**
     * Adds a callback to handle cell value changes in the grid, triggered by all sorts of events
     * that change cell data: that is as currently known
     *   - the 'cellEditEnded' event, which is triggered when a user finishes editing a cell, and
     *   - the 'pastedCell' event, which is triggered when a user pastes data into a cell.

     * @param grid - The FlexGrid instance to attach the handler to.
     * @param callback - The function to be called when a cell value changes.
     */
    static addCellValueChangeHandler(grid: FlexGrid, callback: (s: FlexGrid, e: CellRangeEventArgs) => void) {
        grid.cellEditEnded.addHandler(callback);
        grid.pastedCell.addHandler(callback);
    }

    static hasAnyInputChildElement(e: HTMLElement): boolean {
        for (let i = 0; i < e.children.length; i++) {
            if (e.children[i].tagName === 'INPUT') {
                return true;
            }
        }
        return false;
    }

    static setHeaderRowHeights(grid: FlexGrid, ...heights: number[]) {
        if (grid.columnHeaders.rows.length !== heights.length) {
            throw new Error('Heights array length must match the number of header rows.');
        }
        for (let i = 0; i < heights.length; i++) {
            grid.columnHeaders.rows[i].height = heights[i];
        }
    }

    /**
     * Clears all filters in the specified grid filter and optionally applies the changes
     * so that the grid shows all rows unfiltered.
     */
    static clearGridFilter(gridFilter: FlexGridFilter|undefined, apply: boolean = true) {
        if (!gridFilter) return;
        const nCols = gridFilter.grid.columns.length;
        for (let i = 0; i < nCols; i++) {
            const filter = gridFilter.getColumnFilter(i);
            if (filter) filter.clear();
        }

        if (apply) gridFilter.apply();
    }

    /**
     * Returns the number of active filters in the specified grid filter.
     * This is slightly different from what Mescius has provided in an online article:
     * https://developer.mescius.com/kb/checking-for-active-flexgrid-column-filters-in-angular
     * But it's faster and more efficient.
     * @param gridFilter
     */
    static countActiveFilters(gridFilter: FlexGridFilter): number {
        const filters = (gridFilter as any)._filters as any[];
        const count = filters.filter(f => f.isActive).length;
        return count;
    }

    /**
     * Automatically unselects all cells in the grid when it loses focus.
     * This improves the look of tables, especially when the grid is read-only and possibly
     * multiple grids appear on a page because it will take out the highlighted selected cell.
     */
    static autoUnselectWhenFocusLost(grid: FlexGrid) {
        // when the grid loses focus, we would like to unselect all cells
        // because this results in an only-one-grid-has-an-active-cell behaviour
        grid.lostFocus.addHandler(() => {
            // we need to wait a bit and then check if the grid still has focus
            // because 'ctrl-c' also triggers lostFocus, but in this focus will
            // return to the grid after a little while (10ms is not enough!)
            setTimeout(() => {
                if (!grid.containsFocus()) WjGridUtils.unselectAll(grid)
            }, 100);
        });
        WjGridUtils.unselectAll(grid);
    }

    /**
     * Updates the data source of the given grid while preserving the current selection.
     * Allow the grid not to be initialized yet, because we might be called before ngAfterViewInit.
     */
    static updateGridItemsSourceAndPreserveSelection(grid: FlexGrid | undefined, itemsSource: any) {
        if (grid) {
            const selection = grid.selection;
            grid.itemsSource = itemsSource;
            grid.selection = selection;
        }
    }

    /**
     * Registers a grid to publish the current selected value in the global signal so that
     * it will be updated with the unformatted value of the selected cell in the grid.
     *
     * @param grid - the grid to register
     */
    static registerSelectedCellValueSignal(grid: FlexGrid) {
        grid.selectionChanged.addHandler((s, e) => {
            const selectedCell = grid.selection;

            if (selectedCell && selectedCell.row > -1 && selectedCell.col > -1) {
                const unformattedValue = s.getCellData(selectedCell.row, selectedCell.col, false);
                WjGridUtils._selectedCellUnformattedValue.set(unformattedValue);
            }

            const cellRange = grid.selection;
            const rowRange = range(cellRange.topRow, cellRange.bottomRow + 1);
            const colRange = range(cellRange.leftCol, cellRange.rightCol + 1);

            if (rowRange.length === 0 || colRange.length === 0) {
                WjGridUtils._selectedRangeUnformattedValues.set([[]]);
                WjGridUtils._selectedRangeSummary.set(NO_SELECTION_SUMMARY);
            } else {
                const visibleColumns = colRange.filter(i => grid.getColumn(i)?.isVisible) ?? false;
                let numNumeric = 0;
                let numSelected = 0;
                let sumNumeric: number | undefined = undefined;

                const data = rowRange.map(row => {
                    return visibleColumns.map((col, idx) => {
                        const value = grid.getCellData(row, col, false);
                        if (value != null) {
                            numSelected++;
                            if (typeof value === 'number') {
                                numNumeric++;
                                sumNumeric = (sumNumeric ?? 0) + value;
                            }
                        }
                        return value;
                    });
                });

                WjGridUtils._selectedRangeSummary.set({
                    numSelected, numNumeric, sumNumeric, avgNumeric: sumNumeric ? sumNumeric / numNumeric : undefined,
                });
                WjGridUtils._selectedRangeUnformattedValues.set(data);
            }
        });
    }
}
